import React, { useEffect, useState } from "react";
import {
  getListOfFiles,
  getSpecificFile,
  markUploadAsReviewed,
  aiAdminPreview,
} from "../../../api";
import fileDownload from "js-file-download";
import moment from "moment";
import {
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Center,
  Input,
  InputGroup,
  InputLeftElement,
  Flex,
  Badge,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Stack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  HStack,
  VStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronUpIcon, SearchIcon } from "@chakra-ui/icons";
import { Spinner } from "../../../components";
import AIPreviewTable from "../../uploads/panels/AIUpload/AIPreviewTable";
import { useAtomValue } from "jotai";
import { AccountAtom } from "../../../store";

interface FileData {
  id: number;
  broker: string;
  userEmail: string;
  userId: string;
  createdAt: string;
  status: string;
  adminReviewMessage: string;
}

export default function Files() {
  const accountState = useAtomValue(AccountAtom);
  const previewModal = useDisclosure();
  const [previewTrades, setPreviewTrades] = useState<any[]>([]);
  const [previewCashflows, setPreviewCashflows] = useState<any[]>([]);
  const [previewBroker, setPreviewBroker] = useState<string>("");
  const [previewError, setPreviewError] = useState<string>("");
  const [previewLoading, setPreviewLoading] = useState(false);

  const [files, setFiles] = useState<FileData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [sortColumn, setSortColumn] = useState<string>("createdAt");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const itemsPerPage = 10;

  const getFiles = async () => {
    setLoading(true);
    try {
      const fullListOfFiles = await getListOfFiles();
      setFiles(fullListOfFiles.files);
    } catch (error) {
      console.error("Error fetching files:", error);
    } finally {
      setLoading(false);
    }
  };

  const getSpecificFileFunc = async (id: number) => {
    setLoading(true);
    try {
      const specificFile = await getSpecificFile(id);
      fileDownload(
        specificFile.file.csvContent,
        `${specificFile.file.broker}-${specificFile.file.userId}-${specificFile.file.createdAt}.csv`
      );
    } catch (error) {
      console.error("Error downloading file:", error);
    } finally {
      setLoading(false);
    }
  };

  const runAiPreview = async (id: number) => {
    setPreviewLoading(true);
    setPreviewError("");
    setPreviewTrades([]);
    setPreviewCashflows([]);
    setPreviewBroker("");
    previewModal.onOpen();
    try {
      const res: any = await aiAdminPreview(id);
      if (res?.success === true) {
        setPreviewTrades(res.trades || []);
        setPreviewCashflows(res.cashflows || []);
        setPreviewBroker(res.brokerDetected || "");
      } else {
        setPreviewError(res?.message || "Preview failed");
      }
    } catch (e: any) {
      setPreviewError(e?.message || "Preview failed");
    } finally {
      setPreviewLoading(false);
    }
  };

  const markFileAsReviewed = async (id: number) => {
    setLoading(true);
    try {
      await markUploadAsReviewed(id);
      getFiles();
    } catch (error) {
      console.error("Error marking file as reviewed:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getFiles();
  }, []);

  // Filter files based on search query and status filter
  const filteredFiles = files.filter((file) => {
    const matchesSearch =
      file.broker.toLowerCase().includes(searchQuery.toLowerCase()) ||
      file.userEmail.toLowerCase().includes(searchQuery.toLowerCase()) ||
      file.id.toString().includes(searchQuery.toLowerCase());

    const matchesStatus =
      statusFilter === "all" ||
      file.status.toLowerCase() === statusFilter.toLowerCase();

    return matchesSearch && matchesStatus;
  });

  // Sort files based on column and direction
  const sortedFiles = [...filteredFiles].sort((a, b) => {
    const aValue = a[sortColumn as keyof FileData];
    const bValue = b[sortColumn as keyof FileData];

    if (sortColumn === "createdAt") {
      return sortDirection === "asc"
        ? new Date(aValue as string).getTime() -
            new Date(bValue as string).getTime()
        : new Date(bValue as string).getTime() -
            new Date(aValue as string).getTime();
    }

    if (typeof aValue === "string" && typeof bValue === "string") {
      return sortDirection === "asc"
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    // For numeric values
    return sortDirection === "asc"
      ? Number(aValue) - Number(bValue)
      : Number(bValue) - Number(aValue);
  });

  // Paginate files
  const paginatedFiles = sortedFiles.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(sortedFiles.length / itemsPerPage);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  if (loading && files.length === 0) {
    return (
      <Card variant="elevated" width="100%">
        <CardBody>
          <Center flexDirection="column" h="100%" p={10}>
            <Spinner size="xl" mb="10px" />
            <Text fontSize="lg" fontWeight="medium">
              Loading uploads...
            </Text>
          </Center>
        </CardBody>
      </Card>
    );
  }

  const SortIcon = ({ column }: { column: string }) => {
    if (sortColumn !== column) return null;
    return sortDirection === "asc" ? (
      <ChevronUpIcon ml={1} />
    ) : (
      <ChevronDownIcon ml={1} />
    );
  };

  return (
    <Card variant="elevated" width="100%">
      <CardHeader>
        <Heading size="md" display="flex" alignItems="center">
          <Text mr={2}>📁</Text>
          File Management
        </Heading>
        <Flex direction={{ base: "column", sm: "row" }} gap={4} mt={4}>
          <Box flex="1" position="relative">
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="Search by broker, email or ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </InputGroup>
          </Box>
          <Menu>
            <MenuButton
              as={Button}
              rightIcon={<ChevronDownIcon />}
              width={{ base: "100%", sm: "auto" }}
              variant="outline"
            >
              Status: {statusFilter === "all" ? "All" : statusFilter}
            </MenuButton>
            <MenuList>
              <MenuItem onClick={() => setStatusFilter("all")}>All</MenuItem>
              <MenuItem onClick={() => setStatusFilter("REVIEWED")}>
                Reviewed
              </MenuItem>
              <MenuItem onClick={() => setStatusFilter("PENDING")}>
                Pending
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      </CardHeader>
      <CardBody>
        <Box borderWidth="1px" borderRadius="lg" overflow="hidden">
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th
                  cursor="pointer"
                  onClick={() => handleSort("broker")}
                  userSelect="none"
                >
                  <Flex align="center">
                    Broker <SortIcon column="broker" />
                  </Flex>
                </Th>
                <Th
                  cursor="pointer"
                  onClick={() => handleSort("userEmail")}
                  userSelect="none"
                >
                  <Flex align="center">
                    User Email <SortIcon column="userEmail" />
                  </Flex>
                </Th>
                <Th>User ID</Th>
                <Th
                  cursor="pointer"
                  onClick={() => handleSort("createdAt")}
                  userSelect="none"
                >
                  <Flex align="center">
                    Uploaded At <SortIcon column="createdAt" />
                  </Flex>
                </Th>
                <Th>Download</Th>
                <Th>
                  <HStack spacing={2}>
                    <span>AI preview</span>
                    <Badge
                      colorScheme="purple"
                      fontSize="0.65em"
                      textTransform="uppercase"
                      letterSpacing="0.06em"
                    >
                      Alpha
                    </Badge>
                  </HStack>
                </Th>
                <Th>Status</Th>
                <Th>Message</Th>
              </Tr>
            </Thead>
            <Tbody>
              {paginatedFiles.length > 0 ? (
                paginatedFiles.map((file) => (
                  <Tr key={file.id}>
                    <Td fontWeight="medium">{file.broker}</Td>
                    <Td>{file.userEmail}</Td>
                    <Td fontFamily="mono" fontSize="xs">
                      {file.id}
                    </Td>
                    <Td>{moment(file.createdAt).calendar()}</Td>
                    <Td>
                      <Button
                        onClick={() => getSpecificFileFunc(file.id)}
                        size="sm"
                        colorScheme="blue"
                        variant="outline"
                        leftIcon={<Text>📥</Text>}
                      >
                        Download
                      </Button>
                    </Td>
                    <Td>
                      <Button
                        onClick={() => runAiPreview(file.id)}
                        size="sm"
                        colorScheme="purple"
                        variant="outline"
                      >
                        Preview
                      </Button>
                    </Td>
                    <Td>
                      <Button
                        size="sm"
                        colorScheme={
                          file.status === "REVIEWED" ? "green" : "orange"
                        }
                        variant={
                          file.status === "REVIEWED" ? "outline" : "solid"
                        }
                        onClick={() => markFileAsReviewed(file.id)}
                      >
                        {file.status}
                      </Button>
                    </Td>
                    <Td>
                      {file.adminReviewMessage ? (
                        <Badge variant="outline" colorScheme="purple">
                          {file.adminReviewMessage}
                        </Badge>
                      ) : (
                        <Text fontSize="sm" color="gray.500">
                          No message
                        </Text>
                      )}
                    </Td>
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan={8} height="24" textAlign="center">
                    {loading ? (
                      <Flex justifyContent="center" alignItems="center">
                        <Spinner mr={2} />
                        Loading...
                      </Flex>
                    ) : (
                      "No files found."
                    )}
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </Box>

        {totalPages > 1 && (
          <Flex mt={4} justify="center">
            <HStack>
              <Button
                size="sm"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                isDisabled={currentPage === 1}
              >
                Previous
              </Button>

              <HStack spacing={1}>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (page) => (
                    <Button
                      key={page}
                      size="sm"
                      variant={currentPage === page ? "solid" : "outline"}
                      colorScheme={currentPage === page ? "blue" : "gray"}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </Button>
                  )
                )}
              </HStack>

              <Button
                size="sm"
                onClick={() =>
                  setCurrentPage(Math.min(totalPages, currentPage + 1))
                }
                isDisabled={currentPage === totalPages}
              >
                Next
              </Button>
            </HStack>
          </Flex>
        )}

        <Modal
          isOpen={previewModal.isOpen}
          onClose={previewModal.onClose}
          size="6xl"
          scrollBehavior="inside"
        >
          <ModalOverlay />
          <ModalContent maxH="90vh">
            <ModalHeader>
              <HStack spacing={3} flexWrap="wrap" align="center">
                <span>AI mapping preview</span>
                <Badge
                  colorScheme="purple"
                  fontSize="0.7em"
                  textTransform="uppercase"
                  letterSpacing="0.06em"
                >
                  Alpha
                </Badge>
              </HStack>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              {previewLoading && (
                <Center py={10}>
                  <Spinner mr={3} />
                  Running AI mapper…
                </Center>
              )}
              {previewError && (
                <Text color="red.400" mb={4}>
                  {previewError}
                </Text>
              )}
              {!previewLoading && previewBroker && (
                <Text fontSize="sm" color="gray.500" mb={2}>
                  Detected broker: <strong>{previewBroker}</strong>
                </Text>
              )}
              {!previewLoading && previewTrades.length > 0 && (
                <AIPreviewTable
                  trades={previewTrades}
                  cashflows={previewCashflows}
                  accountId={accountState.selectedAccount?.id}
                  readOnly
                  onRemoveRow={() => {}}
                  onCellChange={() => {}}
                />
              )}
              {!previewLoading &&
                !previewError &&
                previewTrades.length === 0 &&
                previewModal.isOpen && (
                  <Text color="gray.500">No mapped trades returned.</Text>
                )}
            </ModalBody>
          </ModalContent>
        </Modal>

        <Text mt={4} fontSize="sm" color="gray.500">
          Showing {paginatedFiles.length} of {filteredFiles.length} files
          {searchQuery && ` matching "${searchQuery}"`}
          {statusFilter !== "all" && ` with status "${statusFilter}"`}
        </Text>
      </CardBody>
    </Card>
  );
}
